import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import {
    ProductTileAd,
    ProductTileAdPlaceholder,
} from 'dibs-google-ad-manager/exports/ProductTileAd.tsx';
import { getProductTileAdMap } from '../../utils/adHelpers.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { getPageAdData } from '../hooks/useMergedSbAndSponsoredItems.ts';

import classnames from 'classnames';
import styles from './SbSharedProductAdWrapper.module.css';
import dibsCss from 'dibs-css';

import { type SbSharedProductAdWrapper_itemSearch$key } from './__generated__/SbSharedProductAdWrapper_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedProductAdWrapper_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        sponsored {
            maxAvailable
        }
    }
`;
type Props = {
    itemSearch: SbSharedProductAdWrapper_itemSearch$key;
    index: number;
    pageCol?: number;
    isPlaceholder?: boolean;
};

export const SbSharedProductAdWrapper: FC<Props> = ({
    itemSearch: itemSearchRef,
    index,
    pageCol,
    isPlaceholder = false,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const relayVariables = useSbSelector(state => state.relayVariables.variables);
    const { isDesktop, areDisplayAdsEnabled, isQuickFiltersRedesignVariant } =
        useServerVarsContext();
    // reading isTrade value from relay vars b/c we need to get correct ad config values
    // during first render when google tag script starts loading ads.
    const showTradeAds = relayVariables.isTrade;
    const { pageHasSponsoredItems } = getPageAdData({
        maxAvailable: itemSearch?.sponsored?.maxAvailable,
        page: relayVariables.page,
        isDesktop,
        isQuickFiltersRedesignVariant,
    });

    const adMap = getProductTileAdMap({
        uriRef: itemSearch?.displayUriRef || '',
        pageCol,
        pageSize: relayVariables.first ?? 60,
        userType: showTradeAds ? TRADE : CONSUMER,
        deviceType: isDesktop ? 'desktop' : 'mobile',
        areDisplayAdsEnabled,
        pageHasSponsoredItems,
        isQuickFiltersRedesignVariant,
    });

    const ad = adMap[index + 1]; // 1-indexed

    if (!ad) {
        return null;
    }
    const { width, height, widthHold, heightHold } = ad;

    const containerClassName = classnames(dibsCss.minW0, dibsCss.overflowHidden, {
        [styles.mobileTileContainer]: !isDesktop,
    });

    if (isPlaceholder) {
        return (
            <ProductTileAdPlaceholder
                width={width}
                height={height}
                widthHold={widthHold}
                heightHold={heightHold}
                className={containerClassName}
            />
        );
    }

    const { adId, elementId, sizes } = ad;
    return (
        <ProductTileAd
            adId={adId}
            elementId={elementId}
            height={height}
            heightHold={heightHold}
            width={width}
            widthHold={widthHold}
            lazyLoad
            sizes={sizes}
            scaleToFit
            containerClassName={containerClassName}
        />
    );
};
